import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Lead } from '../@types/lead'
import { SprengnetterCategory, ValuationResponse } from '../@types/valuation'
import { RealEstateType } from '../@types/realEstateType'
import { getSprengnetterLocationByPostcode } from './geoClient'
import { ApartmentSubtype, HouseSubtype } from '../@types/realEstateSubtype'
import { Furnishing } from '../@types/FurnishingType'
import { sendErrorLog } from './logClient'
import { ErrorLogSource } from '../@types/errorLog'
import { STEP } from '../@types/state'

dayjs.extend(utc)

export interface ValuationRequest {
  date: string
  living_area?: number
  plot_area?: number
  construction?: string
  equipment?: {
    value?: string
  }
  construction_year?: number
  rooms?: number
  category: string
  address: {
    nation: 'DE'
    zip: string
    town: string
    house_number: string
    street: string
  }
  valuationForStep?: STEP
}

export interface SprengnetterLocation {
  postcode: string
  city: string
  street: string
  houseNumber?: string
}

const VALUATION_API_URL = `${process.env.RLE_BACKEND_URL}/valuation`

export interface GetValuationRequest {
  lead: Lead
  postcode: string
  city?: string
  street?: string
  houseNumber?: string
  valuationForStep?: STEP
}
export const getValuation = async (request: GetValuationRequest): Promise<ValuationResponse> =>
  fetch(`${VALUATION_API_URL}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(await mapLeadToValuationRequest(request)),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`${response.status}`)
      return response.json()
    })
    .catch((error) => {
      const errorMessage = `Error while getting valuation, message: ${error.message}`
      sendErrorLog({
        message: errorMessage,
        source: ErrorLogSource.VALUATION,
      })
      throw new Error(errorMessage)
    })

export const mapToSprengnetterCategory = (realEstateType?: RealEstateType): SprengnetterCategory => {
  switch (realEstateType) {
    case RealEstateType.APARTMENT:
      return SprengnetterCategory.APARTMENT
    case RealEstateType.HOUSE:
      return SprengnetterCategory.SINGLE_FAMILY_HOUSE
    case RealEstateType.LAND:
      return SprengnetterCategory.LAND
    case RealEstateType.COMMERCIAL:
      return SprengnetterCategory.COMMERCIAL
    default:
      return SprengnetterCategory.SINGLE_FAMILY_HOUSE
  }
}

const furnishingTypes = {
  [Furnishing.SIMPLE]: 'EINFACH',
  [Furnishing.NORMAL]: 'MITTEL',
  [Furnishing.HIGH_QUALITY]: 'GEHOBEN',
  [Furnishing.LUXURIOUS]: 'STARK_GEHOBEN',
}

const realEstateSubTypes = {
  [HouseSubtype.SEMI_DETACHED_HOUSE]: 'DOPPELHAUS',
  [HouseSubtype.DETACHED_HOUSE]: 'FREISTEHEND',
  [HouseSubtype.MULTI_FAMILY_HOUSE]: 'REIHEN_ENDHAUS',
  [HouseSubtype.TERRACED_HOUSE]: 'REIHEN_MITTELHAUS',
  [ApartmentSubtype.APARTMENT]: 'ETAGENWOHNUNG',
  [ApartmentSubtype.ATTIC]: 'DACHGESCHOSS',
  [ApartmentSubtype.BASEMENT]: 'SOUTERRAIN',
  [ApartmentSubtype.LOFT]: 'LOFT',
  [ApartmentSubtype.GROUND_FLOOR]: 'ERDGESCHOSSWOHNUNG',
  [ApartmentSubtype.DUPLEX]: 'MAISONETTE',
}

export const mapFurnishingType = (furnishingType?: Furnishing): string | undefined => {
  if (furnishingType) return furnishingTypes[furnishingType]
}

export const mapRealEstateSubType = (realEstateSubType?: HouseSubtype | ApartmentSubtype): string | undefined => {
  if (realEstateSubType) return realEstateSubTypes[realEstateSubType]
}

export const validateLocation = (request: GetValuationRequest): boolean =>
  !!(request.postcode && request.city && request.street)

export const mapPropertyLocationToSprengnetterLocation = (request: GetValuationRequest): SprengnetterLocation => {
  return {
    postcode: request.postcode,
    city: request.city ? request.city : '',
    street: request.street ? request.street : '',
    houseNumber: request.houseNumber,
  }
}

export const mapLeadToValuationRequest = async (request: GetValuationRequest): Promise<ValuationRequest> => {
  const sprengnetterLocation = await (validateLocation(request)
    ? mapPropertyLocationToSprengnetterLocation(request)
    : getSprengnetterLocationByPostcode(request.postcode))

  const { lead } = request
  const equipment = mapFurnishingType(lead.furnishingType)

  return {
    date: dayjs().utc().format(),
    living_area: lead.livingSpace,
    plot_area: lead.plotArea,
    construction: mapRealEstateSubType(lead.realEstateSubtype),
    equipment: equipment ? { value: equipment } : undefined,
    construction_year: lead.constructionYear,
    rooms: lead.rooms,
    category: mapToSprengnetterCategory(lead.realEstateType),
    address: {
      nation: 'DE',
      zip: sprengnetterLocation.postcode,
      town: sprengnetterLocation.city,
      house_number: sprengnetterLocation.houseNumber ? sprengnetterLocation.houseNumber : '1',
      street: sprengnetterLocation.street,
    },
    valuationForStep: request.valuationForStep,
  }
}
